import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { addWeeks, endOfDay, endOfMonth, startOfDay, startOfMonth } from 'date-fns';
import { useVirtualizer } from '@tanstack/react-virtual';
import Typography from 'antd/lib/typography';
import Radio from 'antd/lib/radio';
import DatePicker from 'antd/lib/date-picker';
import Spin from 'antd/lib/spin';
import Empty from 'antd/lib/empty';

import { ISelectOption } from '../../../domain/saved-filters/interface';
import { IDateRange } from '../../../pages/interface';
import { BaseFilterAccordion } from '../base/BaseFilterAccordion';
import { formatInISOString } from '../../../domain/date/date';
import { parseDateRangeValue } from '../../../pages/settlements/view/vendorBillItems/sidebar/utils';

const MAX_WIDTH = 160; // Maximum width for the select box

interface IDateRangeWithCustomOptionFilterSelectProps {
    options: ISelectOption<string>[];
    value: string | null;
    onChange: (value: string | null) => void;
    label?: string;
    loading?: boolean;
    disabled?: boolean;
    height?: number;
    allowClear?: boolean;
    tooltipTitle?: string;
    maxRangeInDays?: number;
    dateFormat?: string;
    pickerMode?: 'date' | 'month';
}

export function DateRangeWithCustomOptionFilterSelect({
    options,
    value,
    onChange,
    label,
    height = 300,
    disabled = false,
    loading = false,
    allowClear = true,
    tooltipTitle,
    maxRangeInDays,
    dateFormat = 'DD/MMM/YYYY',
    pickerMode = 'date',
}: IDateRangeWithCustomOptionFilterSelectProps) {
    dayjs.extend(utc);

    const defaultRange =
        pickerMode === 'month'
            ? {
                  start: startOfMonth(new Date()),
                  end: endOfMonth(new Date()),
              }
            : {
                  start: startOfDay(new Date()),
                  end: endOfDay(addWeeks(new Date(), 1)),
              };

    const [dateRange, setDateRange] = useState<IDateRange>(defaultRange);

    useEffect(() => {
        const dateRange = parseDateRangeValue(value);

        setDateRange(dateRange || defaultRange);
    }, [value]);

    const handleRadioChange = (receivedValue: string | null) => {
        if (receivedValue === value || value?.includes(receivedValue || '')) {
            onChange(null);
            return;
        }

        if (receivedValue === 'custom_range') {
            // for custom range value we use such structure 'key::period'
            onChange(`custom_range::${formatInISOString(dateRange.start)}_${formatInISOString(dateRange.end)}`);
            return;
        }

        onChange(receivedValue);
    };

    const handleDateChange = (dates: any, formattedDate: [string, string]) => {
        let selectedRange = { start: startOfDay(new Date(formattedDate[0]), {}), end: endOfDay(new Date(formattedDate[1])) };

        if (pickerMode === 'month') {
            selectedRange = {
                start: startOfMonth(dayjs(formattedDate[0], 'MMM YYYY').toDate(), {}),
                end: endOfMonth(dayjs(formattedDate[1], 'MMM YYYY').toDate()),
            };
        }

        // for custom range value we use such structure 'key::period'
        onChange(`custom_range::${formatInISOString(selectedRange.start)}_${formatInISOString(selectedRange.end)}`);
    };

    const handleClear = () => handleRadioChange(null);

    // Disabled date function to limit range to maxRangeInDays
    const disabledDate = (currentDate: dayjs.Dayjs, info: { from?: dayjs.Dayjs }) => {
        if (!currentDate || !info.from || !maxRangeInDays) return false;

        // Calculate the difference in days
        const diffDays = Math.abs(currentDate.diff(info.from, 'day'));

        // Disable if the difference exceeds maxRangeInDays
        return diffDays > maxRangeInDays;
    };

    return (
        <BaseFilterAccordion label={label} value={value} onClear={handleClear} showClear={allowClear} tooltipTitle={tooltipTitle}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0,
                    height: value?.includes('custom_range') ? height + 30 : height,
                    position: 'relative',
                }}
            >
                <div style={{ flex: 1, overflow: 'hidden', height: height }}>
                    {/* Loading */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Spin spinning />
                        </div>
                    ) : null}

                    {/* No options */}
                    {!loading && options.length === 0 ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        </div>
                    ) : null}

                    {/* Options List */}
                    {!loading && options.length !== 0 && (
                        <VirtualizedOptionsList
                            height={height}
                            options={options}
                            value={value}
                            disabled={disabled}
                            onRadioChange={handleRadioChange}
                        />
                    )}
                </div>

                {value?.includes('custom_range') && (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <DatePicker.RangePicker
                            picker={pickerMode}
                            size={'small'}
                            suffixIcon={false}
                            value={[dayjs.utc(dateRange.start), dayjs.utc(dateRange.end.toISOString())]}
                            onChange={handleDateChange}
                            format={dateFormat}
                            allowClear={false}
                            changeOnBlur
                            disabledDate={disabledDate}
                        />
                    </div>
                )}
            </div>
        </BaseFilterAccordion>
    );
}

// Virtualized Options List Component
interface VirtualizedOptionsListProps {
    height: number;
    options: ISelectOption<string>[];
    value: string | null;
    disabled: boolean;
    onRadioChange: (value: string) => void;
}

function VirtualizedOptionsList<T = number>({ height, options, value, disabled, onRadioChange }: VirtualizedOptionsListProps) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: options.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 34, // Adjust based on your option height
        overscan: 5, // Number of items to render outside the visible area
        useAnimationFrameWithResizeObserver: true, // Use animation frame for smoother scrolling
        paddingEnd: 8, // Add some padding to the end for better scrolling experience
    });

    return (
        <div
            ref={parentRef}
            style={{
                height,
                overflow: 'auto',
            }}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map(virtualItem => {
                    const option = options[virtualItem.index];

                    return (
                        <div
                            key={virtualItem.key}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualItem.size,
                                transform: `translateY(${virtualItem.start}px)`,
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8,
                            }}
                        >
                            <Radio
                                disabled={disabled}
                                checked={value?.includes(option.value)}
                                onClick={e => onRadioChange(option.value)}
                                value={option.value}
                            >
                                <Typography.Text
                                    ellipsis={{ tooltip: { title: option.label, placement: 'right' } }}
                                    style={{ fontSize: '12px', lineHeight: '20px', maxWidth: MAX_WIDTH }}
                                >
                                    {option.label}
                                </Typography.Text>
                            </Radio>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
